import { Text, MessageBox } from '@reservamos/elements';
import React, { useCallback, useMemo, useState } from 'react';
import useCostapassState from 'hooks/useCostapassState';
import { useTranslation } from 'react-i18next';
import formatEmailPrivacy from 'utils/formatEmailPrivacy';
import formatPhonePrivacy from 'utils/formatPhonePrivacy';

import CodeInput from './CodeInputs';
import { useRedeemContext } from '../../RedeemHandlerContext';
/**
 * RedeemCodeForm is a component that allows the user to redeem points by entering a code.
 * @returns {JSX.Element} - The RedeemCodeForm component.
 */
const RedeemCodeForm = () => {
  const { onConfirm, onReset, selectedMethod, error, isConfirming } = useRedeemContext();
  const {
    profile: { email, phone },
  } = useCostapassState();
  const [submitted, setSubmitted] = useState(false);
  const [code, setCode] = useState(['', '', '', '']);

  const { t } = useTranslation(['payment', 'general', 'notifications']);

  const isCodeValid = useMemo(() => {
    return code.every((value) => value !== '');
  }, [code]);

  const handleOnConfirm = useCallback(() => {
    setSubmitted(true);
    if (!isCodeValid) return;

    onConfirm(code.join(''));
  }, [onConfirm, isCodeValid, code]);

  const handleOnChange = useCallback(
    ({ index, value }) => {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
    },
    [code],
  );

  const handleOnPaste = useCallback(
    (newCode) => {
      const codeLength = code.length;
      const newCodeArray = newCode.split('');
      const matchedCode = Array.from({ length: codeLength }, (_, index) => newCodeArray[index]);
      setCode(matchedCode);
    },
    [code],
  );

  const methodValue = useMemo(() => {
    return selectedMethod === 'email'
      ? formatEmailPrivacy(email || '')
      : formatPhonePrivacy(phone || '');
  }, [selectedMethod, email, phone]);

  return (
    <div className="flex flex-col pt-2 gap-6">
      <Text color="grayStrong" size="L">
        {t(`wallet_instructions.sent_code_${selectedMethod}`)}
        <strong className="text-primary-regular">{methodValue}. </strong>
        {t(`wallet_instructions.sent_code_${selectedMethod}_2`)}
      </Text>

      <CodeInput
        code={code}
        isLoading={isConfirming}
        submitted={submitted}
        onChange={handleOnChange}
        onPaste={handleOnPaste}
      />
      {error === 'validateNITCode' && (
        <MessageBox borderColor="error">
          <Text size="L" color="error">
            {t('notifications:error.validating_code')}
          </Text>
        </MessageBox>
      )}

      <div className="flex justify-center">
        <Text color="grayMedium" size="M">
          {t('payment:label.have_not_received_code')}
        </Text>
        <button
          type="button"
          className="text-primary-regular text-base underline border-none bg-transparent ml-1 hover:text-primary-dark"
          onClick={onReset}
        >
          {t('general:button.resend_code')}
        </button>
      </div>
      <button className="w-full custom-gradient-btn" type="button" onClick={handleOnConfirm}>
        {t('general:button.enter_code')}
      </button>
    </div>
  );
};

export default RedeemCodeForm;
